import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetCategoryDTO, AssetDetail, AssetRelationDTO, AssetSummary, AttachmentDTO, Cue, CueIssue, TrackStyle, MetadataSchemaDefinition, MetadataSchemaDTO, RecycleBinSettingsDTO, SchemaChange,
  SegmentDTO, ShareLinkDTO, StratumDTO,
} from '@prism/contracts';
import { api } from '../client';
import type { ActivityKind, AuditEvent } from '../../lib/activity';
import { queryKeys } from '../queryKeys';

/** An earlier version of an asset in the recycle bin (1.2.3; GET /api/v1/recycle-bin `versions`). */
export interface BinVersion {
  versionId: string; number: number; assetId: string; title: string; originalFilename: string | null;
  deletedAt: string; deletedBy: string | null; purgeAfter: string | null; purgeError: string | null; purging: boolean; sizeBytes: number;
}

export function useAsset(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.asset(id),
    queryFn: () => api<AssetDetail>(`/api/v1/assets/${id}`),
    enabled: Boolean(id),
    // Media URLs carry short-lived tokens; refresh before they expire.
    refetchInterval: (q) => {
      // A recording (1.0.0-rc.1): the length recorded so far grows, and the proxy arrives when it has ended.
      const d = q.state.data;
      if (d && !d.media.proxyUrl && (d.live?.state === 'recording' || d.live?.state === 'finishing')) return 5000;
      const exp = q.state.data?.media.expiresAt;
      return exp ? Math.max(30_000, new Date(exp).getTime() - Date.now() - 120_000) : false;
    },
  });
}

export function useSchema() {
  return useQuery({
    queryKey: queryKeys.schema(),
    queryFn: () => api<MetadataSchemaDTO & { editable: Record<string, boolean> }>('/api/v1/metadata-schema'),
    staleTime: 60_000,
  });
}

interface AdminModel {
  current: { id: string; version: number; publishedAt: string; definition: MetadataSchemaDefinition };
  versions: Array<{ version: number; publishedAt: string; createdBy: string | null; changes: SchemaChange[] }>;
  usage: Record<string, number>;
  assetsWithArchivedValues: number;
}

export function useAdminModel(enabled = true) {
  return useQuery({ queryKey: queryKeys.adminModel(), queryFn: () => api<AdminModel>('/api/v1/admin/metadata-schema'), enabled });
}

export const ACTIVITY_PAGE_SIZE = 50;

/** The asset's activity log, newest first, ACTIVITY_PAGE_SIZE at a time (0.99.1); fetchNextPage loads older events. */
export function useAssetAudit(id: string, kind: ActivityKind = 'all') {
  return useInfiniteQuery({
    queryKey: queryKeys.assetAudit(id, kind),
    queryFn: ({ pageParam }) => api<{ items: AuditEvent[]; nextCursor: string | null }>(
      `/api/v1/assets/${id}/audit?limit=${ACTIVITY_PAGE_SIZE}&kind=${kind}${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ''}`,
    ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useAssetAction(action: 'retry' | 'dismiss' | 'restore' | 'process') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/v1/assets/${id}/${action}`, { method: 'POST', json: {} }),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: queryKeys.assets() });
      void qc.invalidateQueries({ queryKey: queryKeys.asset(id) });
    },
  });
}

export function useAttachments(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.attachments(assetId),
    queryFn: () => api<{ items: AttachmentDTO[] }>(`/api/v1/assets/${assetId}/attachments`),
    enabled: Boolean(assetId),
  });
}

/* ------------------------------------------------------------------ timecode metadata */

/** The layers. `counts` (1.8.1, Administration → Layers only): each one's segments too, which the API counts only when asked. */
export function useStrata(opts: { counts?: boolean } = {}) {
  return useQuery({
    queryKey: opts.counts ? queryKeys.strataCounts() : queryKeys.strata(),
    queryFn: () => api<{ items: StratumDTO[] }>(opts.counts ? '/api/v1/strata?counts=true' : '/api/v1/strata'),
    staleTime: 60_000,
  });
}

export function useSegments(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.segments(assetId),
    queryFn: () => api<{ versionId: string; items: SegmentDTO[] }>(`/api/v1/assets/${assetId}/segments`),
    enabled: Boolean(assetId),
  });
}

/* ------------------------------------------------------------------ delete and recycle bin */

export function useRecycleBin(q = '', enabled = true) {
  return useQuery({
    queryKey: queryKeys.recycleBin(q),
    // `versions` (1.2.3): earlier versions of assets in the bin; absent from servers before 1.2.3.
    queryFn: () => api<{ items: AssetSummary[]; total: number; versions?: BinVersion[]; settings: RecycleBinSettingsDTO }>(`/api/v1/recycle-bin${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    enabled,
    refetchInterval: 15_000,
  });
}

export function useRecycleSettings(enabled = true) {
  return useQuery({ queryKey: queryKeys.recycleSettings(), queryFn: () => api<RecycleBinSettingsDTO>('/api/v1/settings/recycle-bin'), staleTime: 60_000, enabled });
}

/** Delete, restore or purge; every one refreshes the library, the bin and the asset. */
export function useDeleteActions() {
  const qc = useQueryClient();
  const refresh = (id: string) => {
    void qc.invalidateQueries({ queryKey: queryKeys.assets() });
    void qc.invalidateQueries({ queryKey: queryKeys.recycleBins() });
    void qc.invalidateQueries({ queryKey: queryKeys.asset(id) });
    void qc.invalidateQueries({ queryKey: queryKeys.assetAudit(id) });
    // A graphics asset leaves (or comes back to) the Graphics area too.
    void qc.invalidateQueries({ queryKey: queryKeys.graphicsAll() });
  };
  return {
    remove: useMutation({
      mutationFn: ({ id, confirmInUse = false }: { id: string; confirmInUse?: boolean }) =>
        api<{ recycleBin: boolean; purgeAfter: string }>(`/api/v1/assets/${id}/delete`, { method: 'POST', json: { confirmInUse } }),
      onSuccess: (_d, v) => refresh(v.id),
    }),
    restore: useMutation({
      mutationFn: (id: string) => api(`/api/v1/recycle-bin/${id}/restore`, { method: 'POST', json: {} }),
      onSuccess: (_d, id) => refresh(id),
    }),
    purge: useMutation({
      mutationFn: (id: string) => api(`/api/v1/recycle-bin/${id}/purge`, { method: 'POST', json: {} }),
      onSuccess: (_d, id) => { refresh(id); qc.removeQueries({ queryKey: queryKeys.asset(id) }); },
    }),
  };
}

export function useSaveMetadata(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { revision: number; values: Record<string, unknown> }) =>
      api<AssetDetail>(`/api/v1/assets/${id}/metadata`, { method: 'PATCH', json: body }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.asset(id), data);
      void qc.invalidateQueries({ queryKey: queryKeys.assets() });
      void qc.invalidateQueries({ queryKey: queryKeys.assetAudit(id) });
    },
  });
}

/* ------------------------------------------------------------------ subtitles */

export interface SubtitleTrackDTO {
  id: string;
  language: string;
  label: string;
  kind: 'subtitles' | 'captions';
  /** 1.9.0: the track's default style ({} when none); cues may set their own (Cue.style). */
  style: TrackStyle;
  revision: number;
  source: string;
  cues: Cue[];
  issues: CueIssue[];
  updatedAt: string;
  updatedBy: string | null;
}

export function useSubtitles(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.subtitles(assetId),
    queryFn: () => api<{ items: SubtitleTrackDTO[] }>(`/api/v1/assets/${assetId}/subtitles`),
    enabled: Boolean(assetId),
  });
}

/** One request to generate subtitles from the audio (Whisper). */
export interface TranscriptionDTO {
  id: string;
  versionId: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  /** What was asked for: auto, fi, sv or en. */
  language: string;
  detectedLanguage: string | null;
  trackId: string | null;
  cueCount: number | null;
  error: { code: string; message: string; retryable: boolean; hint?: string } | null;
  requestedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** Under the subtitles key so the asset.subtitles_changed event refreshes it too. */
export function useTranscriptions(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.transcriptions(assetId),
    queryFn: () => api<{ items: TranscriptionDTO[] }>(`/api/v1/assets/${assetId}/transcriptions`),
    enabled: Boolean(assetId),
    // The event refreshes it as soon as the job changes; this only covers a missed event.
    refetchInterval: (q) => (q.state.data?.items.some((t) => t.status === 'queued' || t.status === 'running') ? 20_000 : false),
  });
}

/* ------------------------------------------------------------------ classes, relations, sharing */

export function useCategories() {
  return useQuery({ queryKey: queryKeys.categories(), queryFn: () => api<{ items: AssetCategoryDTO[] }>('/api/v1/asset-categories'), staleTime: 60_000 });
}
export function useRelations(assetId: string) {
  return useQuery({ queryKey: queryKeys.relations(assetId), queryFn: () => api<{ items: AssetRelationDTO[] }>(`/api/v1/assets/${assetId}/relations`) });
}
export function useShares(assetId: string, enabled = true) {
  return useQuery({ queryKey: queryKeys.shares(assetId), queryFn: () => api<{ items: ShareLinkDTO[] }>(`/api/v1/assets/${assetId}/shares`), enabled });
}
